import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { extractErrorMessage } from '../../shared/utils/errors';
import {
  PaginatedResponse,
  SwapOperation,
  SwapStatus,
} from '../../shared/types';
import CryptoAmountWithUsd from '../../shared/components/CryptoAmountWithUsd';
import { getSwap, listSwaps, SwapFilters } from './api';
import { formatUserLabel, useUserNames } from '../../shared/hooks/useUserNames';

const STATUS_OPTIONS: { value: SwapStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const CURRENCY_OPTIONS = ['', 'BTC', 'ETH', 'USDT', 'TRX'];

const statusBadge = (status: SwapStatus): string => {
  switch (status) {
    case 'completed':
      return 'badge badge-success';
    case 'pending':
      return 'badge badge-warning';
    case 'failed':
      return 'badge badge-danger';
    case 'cancelled':
      return 'badge bg-slate-700/40 text-slate-300 border border-slate-600';
    default:
      return 'badge';
  }
};

const SMALLEST_UNIT_DIVISOR = 100_000_000;

const formatCryptoAmount = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '0';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value)) return '0';
  const decimal = value / SMALLEST_UNIT_DIVISOR;
  return decimal.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const SwapsPage: React.FC = () => {
  const [swaps, setSwaps] = useState<PaginatedResponse<SwapOperation> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<SwapFilters>({});
  const [draftFilters, setDraftFilters] = useState<SwapFilters>({});

  const [detail, setDetail] = useState<SwapOperation | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const load = useCallback(async (page: number, filtersToUse: SwapFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listSwaps({ ...filtersToUse, page, per_page: 20 });
      setSwaps(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load swap operations'));
      setSwaps(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const openDetail = async (swap: SwapOperation) => {
    setDetail(swap);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const fresh = await getSwap(swap.id);
      setDetail(fresh);
    } catch (err) {
      setDetailError(extractErrorMessage(err, 'Failed to load swap'));
    } finally {
      setDetailLoading(false);
    }
  };

  const filterByUser = (userId: number) => {
    const value = String(userId);
    setDraftFilters((p) => ({ ...p, user_id: value }));
    setFilters((p) => ({ ...p, user_id: value }));
    setCurrentPage(1);
  };

  const userIds: number[] = [];
  if (swaps?.data) {
    for (const s of swaps.data) userIds.push(s.user_id);
  }
  if (detail) userIds.push(detail.user_id);
  const userNames = useUserNames(userIds);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Swap Operations</h1>
            <p className="text-slate-400 mt-1">
              All currency swap operations performed by users.
            </p>
          </div>
        </div>

        <div className="card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              min={1}
              value={draftFilters.user_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, user_id: e.target.value || undefined }))
              }
              className="input py-2"
              placeholder="e.g. 42"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={draftFilters.status ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({
                  ...p,
                  status: (e.target.value || undefined) as SwapStatus | undefined,
                }))
              }
              className="input py-2"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">From</label>
            <select
              value={draftFilters.from_currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, from_currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-from'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">To</label>
            <select
              value={draftFilters.to_currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, to_currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-to'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-xs font-medium text-slate-400 mb-1">Reference</label>
            <input
              type="text"
              value={draftFilters.reference_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, reference_id: e.target.value || undefined }))
              }
              className="input py-2 font-mono text-xs"
              placeholder="SWAP_…"
            />
          </div>
          <div className="flex gap-2 md:col-span-2 lg:col-span-1">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !swaps?.data.length ? (
            <div className="p-12 text-center text-slate-400">No swap operations found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">User</th>
                      <th className="table-header">Pair</th>
                      <th className="table-header text-right">From amount</th>
                      <th className="table-header text-right">To amount</th>
                      <th className="table-header text-right">Rate</th>
                      <th className="table-header text-right">Fee</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Reference</th>
                      <th className="table-header">Created</th>
                      <th className="table-header text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {swaps.data.map((swap) => (
                      <tr key={swap.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{swap.id}</td>
                        <td className="table-cell">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); filterByUser(swap.user_id); }}
                            className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                            title={`Show all swaps for user #${swap.user_id}`}
                          >
                            {formatUserLabel(swap.user_id, userNames)}
                          </button>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">#{swap.user_id}</p>
                        </td>
                        <td className="table-cell text-white whitespace-nowrap">
                          <span className="font-medium">{swap.from_currency}</span>
                          <span className="text-slate-500 mx-1">→</span>
                          <span className="font-medium">{swap.to_currency}</span>
                        </td>
                        <td className="table-cell text-right text-slate-200 font-mono">
                          {formatCryptoAmount(swap.from_amount)}{' '}
                          <span className="text-xs text-slate-500">{swap.from_currency}</span>
                        </td>
                        <td className="table-cell text-right text-slate-200 font-mono">
                          {formatCryptoAmount(swap.to_amount)}{' '}
                          <span className="text-xs text-slate-500">{swap.to_currency}</span>
                        </td>
                        <td className="table-cell text-right text-slate-400 font-mono">
                          {Number(swap.exchange_rate).toLocaleString('en-US', {
                            maximumFractionDigits: 8,
                          })}
                        </td>
                        <td className="table-cell text-right text-slate-400">
                          <div>{Number(swap.fee_percent).toFixed(2)}%</div>
                          <div className="font-mono text-xs mt-0.5">
                            <CryptoAmountWithUsd
                              units={swap.fee_amount}
                              currency={swap.from_currency}
                              showCurrency={false}
                            />
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className={statusBadge(swap.status)}>{swap.status}</span>
                        </td>
                        <td className="table-cell font-mono text-xs text-slate-500 max-w-[180px] truncate">
                          {swap.reference_id}
                        </td>
                        <td className="table-cell text-slate-400 whitespace-nowrap">
                          {formatDateTime(swap.created_at)}
                        </td>
                        <td className="table-cell text-right">
                          <button
                            onClick={() => openDetail(swap)}
                            className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {swaps.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={swaps.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? `Swap #${detail.id}` : ''}
      >
        {detail && (
          <div className="space-y-4 text-sm">
            {detailError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                {detailError}
              </div>
            )}
            {detailLoading && (
              <div className="text-slate-400">Refreshing…</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <DetailRow
                label="User"
                value={(
                  <span>
                    <button
                      type="button"
                      onClick={() => { filterByUser(detail.user_id); setDetail(null); }}
                      className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                      title={`Show all swaps for user #${detail.user_id}`}
                    >
                      {formatUserLabel(detail.user_id, userNames)}
                    </button>
                    <span className="text-xs text-slate-500 font-mono ml-2">#{detail.user_id}</span>
                  </span>
                )}
              />
              <DetailRow label="Status" value={<span className={statusBadge(detail.status)}>{detail.status}</span>} />
              <DetailRow label="From" value={`${formatCryptoAmount(detail.from_amount)} ${detail.from_currency}`} />
              <DetailRow label="To" value={`${formatCryptoAmount(detail.to_amount)} ${detail.to_currency}`} />
              <DetailRow label="Exchange rate" value={Number(detail.exchange_rate).toLocaleString('en-US', { maximumFractionDigits: 8 })} />
              <DetailRow
                label="Fee"
                value={(
                  <span>
                    {Number(detail.fee_percent).toFixed(2)}% (
                    <CryptoAmountWithUsd units={detail.fee_amount} currency={detail.from_currency} />
                    )
                  </span>
                )}
              />
              <DetailRow label="Reference" value={<span className="font-mono text-xs">{detail.reference_id}</span>} />
              <DetailRow label="Idempotency key" value={<span className="font-mono text-xs">{detail.idempotency_key}</span>} />
              <DetailRow label="Created at" value={formatDateTime(detail.created_at)} />
              <DetailRow label="Updated at" value={formatDateTime(detail.updated_at)} />
            </div>
            {detail.error_message && (
              <div>
                <p className="text-xs font-medium text-slate-400 mb-1">Error message</p>
                <pre className="p-3 bg-slate-800/60 border border-red-500/30 rounded-lg text-red-300 text-xs whitespace-pre-wrap break-all">
                  {detail.error_message}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
    <p className="text-slate-200 mt-1 break-all">{value}</p>
  </div>
);

export default SwapsPage;
